import type { LucideIcon } from 'lucide-react';
import { Search, MonitorPlay, Facebook, Activity, Globe, Zap, Trophy } from 'lucide-react';
import type { StrategyChannelId } from '@/store';

// Stage 0 is the Foundation tier: pre-launch setup (account connection,
// conversion tracking install) that has no defensible performance lift on
// its own but gates everything after it.
export type ChannelStage = 0 | 1 | 2 | 3 | 4 | 5;

export interface ChannelMeta {
  id: StrategyChannelId;
  label: string;
  description: string;
  icon: LucideIcon;
  accentBg: string;
  accentText: string;
  // Togglable channels appear in the toggle row and grey out when off.
  // Non-togglable sections (Glitch, Awards) are always on and don't show
  // a switch on their card header.
  togglable: boolean;
}

export const CHANNELS: ChannelMeta[] = [
  {
    id: 'glitch',
    label: 'Glitch',
    description: 'Things to do inside the platform itself',
    icon: Zap,
    accentBg: 'bg-[#EDE9FE]',
    accentText: 'text-[#290BD9]',
    togglable: false,
  },
  {
    id: 'search',
    label: 'Google Search',
    description: 'Search ad goals across structure, bidding, and assets',
    icon: Search,
    accentBg: 'bg-[#EDE9FE]',
    accentText: 'text-[#290BD9]',
    togglable: true,
  },
  {
    id: 'display',
    label: 'Google Display',
    description: 'Display campaigns, audiences, and retargeting',
    icon: MonitorPlay,
    accentBg: 'bg-[#DDD6FE]',
    accentText: 'text-[#5B21B6]',
    togglable: true,
  },
  {
    id: 'meta',
    label: 'Meta Ads',
    description: 'Facebook and Instagram campaigns',
    icon: Facebook,
    accentBg: 'bg-[#DBEAFE]',
    accentText: 'text-[#1D4ED8]',
    togglable: true,
  },
  {
    id: 'tracking',
    label: 'Conversion Tracking',
    description: 'Tracking, attribution, and data quality',
    icon: Activity,
    accentBg: 'bg-[#D1FAE5]',
    accentText: 'text-[#047857]',
    togglable: true,
  },
  {
    id: 'website',
    label: 'Website',
    description: 'Landing pages, CRO, and site performance',
    icon: Globe,
    accentBg: 'bg-[#FEF3C7]',
    accentText: 'text-[#A16207]',
    togglable: true,
  },
  {
    id: 'awards',
    label: 'Awards',
    description: 'Prestige milestones earned from real account performance',
    icon: Trophy,
    accentBg: 'bg-[#FEF3C7]',
    accentText: 'text-[#A16207]',
    togglable: false,
  },
];

export interface ChannelGoal {
  id: string;
  channel: StrategyChannelId;
  stage: ChannelStage;
  title: string;
  description: string;
  route?: string;
  hash?: string;
  // Stable key the backend goal-check resolver reports against. Present only
  // on Tier A goals the system can auto-verify; absent goals stay
  // manual-checkbox only. Decoupled from `id` (which is positional) so the
  // backend contract survives goal reordering.
  autoCheckKey?: string;
}

interface RawGoal {
  channel: StrategyChannelId;
  stage: ChannelStage;
  title: string;
  description: string;
  route?: string;
  hash?: string;
  autoCheckKey?: string;
}

// Only confirmed goals are kept (the funnel was filtered down to the
// `confirm: "Yes"` set). Display, Meta, Awards, and the unconfirmed
// Stage 4–5 / Search Stage 2-3 entries were dropped during that pass.
const RAW: RawGoal[] = [
  // Glitch — in-platform actions (always visible, no channel toggle)
  { channel: 'glitch', stage: 0, title: 'Connect your Google Ads account', description: 'Link a Google Ads account so the platform can read campaigns and act on them.', route: '/settings/integrations', autoCheckKey: 'glitch-account-linked' },
  { channel: 'glitch', stage: 0, title: 'Complete the onboarding wizard', description: 'Finish onboarding so business profile and brand voice power downstream actions.', route: '/new/onboarding', autoCheckKey: 'glitch-onboarding-complete' },
  { channel: 'glitch', stage: 1, title: 'Create your first campaign', description: 'Launch a campaign through the platform so structure and tracking are ready to optimise.', route: '/new/campaign', autoCheckKey: 'glitch-first-campaign' },
  { channel: 'glitch', stage: 1, title: 'Set a brand voice', description: 'Configure brand voice so AI-generated headlines and descriptions stay on-tone.', route: '/business', autoCheckKey: 'glitch-brand-voice' },
  { channel: 'glitch', stage: 1, title: 'Set your monthly budget cap', description: 'Define the maximum monthly spend ceiling for the account so daily pacing and platform allocations stay inside one number you control.', route: '/business', autoCheckKey: 'glitch-monthly-budget-cap' },
  { channel: 'glitch', stage: 1, title: 'Allocate budget to Google Ads', description: 'Decide how much of the monthly cap goes to Google Ads so the bidder has a clean ceiling to work inside.', route: '/business', autoCheckKey: 'glitch-google-ads-allocation' },
  { channel: 'glitch', stage: 1, title: 'Set per-campaign budgets', description: 'Distribute each platform’s budget across active campaigns so the bidder has clean signal on where to spend.', route: '/campaigns', autoCheckKey: 'glitch-per-campaign-budgets' },
  { channel: 'glitch', stage: 1, title: 'Set your primary KPI target', description: 'Pick the KPI to optimise toward (CPA, ROAS, conversions, or spend) and set the target value the advisor will steer against.', route: '/business', hash: 'goals', autoCheckKey: 'glitch-kpi-target' },

  // Google Search — Stage 1 (Foundation, pre–automated bidding)
  { channel: 'search', stage: 1, title: 'Brand campaign live', description: 'A Brand campaign protects branded searches from competitors and captures the cheapest, highest-intent traffic in the account.', route: '/new/campaign/brand', autoCheckKey: 'search-brand-campaign-live' },
  { channel: 'search', stage: 1, title: 'Prospecting campaign live', description: 'A Prospecting campaign targets the highest-intent non-branded customer searches — the primary acquisition driver in the account.', route: '/new/campaign/prospecting', autoCheckKey: 'search-prospecting-campaign-live' },
  { channel: 'search', stage: 1, title: 'Account-level negative keyword list applied', description: 'A shared negative keyword list is applied to every campaign so obvious waste is cut before it spends.' },
  { channel: 'search', stage: 1, title: 'Brand terms blocked from Prospecting', description: 'Brand keywords are excluded as negative exact in Prospecting so generic searches do not eat brand traffic and dilute reporting.' },
  { channel: 'search', stage: 1, title: 'Maximise Clicks bidding with cost caps', description: 'Starting bid strategy while conversion volume is low. Focus stays on Quality Score and search-term hygiene before automated bidding is enabled.', autoCheckKey: 'search-max-clicks-bidding' },
  { channel: 'search', stage: 1, title: 'Tight ad groups with keyword-anchored ads', description: 'Each ad group covers one theme with 10–15 keywords and 2 ads. One headline matches search intent; other headlines test value props, social proof, and CTAs.', autoCheckKey: 'search-tight-ad-groups' },
  // Google Search — Stage 2 (Stabilisation, 30+ conv/month stable)
  { channel: 'search', stage: 2, title: 'Maximise Conversions bidding active', description: 'Bidding advanced once conversion volume is steady (around 30+ per month). Unconstrained at first so the bidder can learn what converts.', autoCheckKey: 'search-max-conversions-bidding' },
  { channel: 'search', stage: 2, title: 'Sitelinks and callouts configured', description: 'Four to six sitelinks per campaign and callouts for differentiators and offers. The two highest-impact ad assets.', autoCheckKey: 'search-assets-configured' },
  { channel: 'search', stage: 2, title: 'Weekly search terms sweep cadence', description: 'A weekly review of search terms: converting queries are added as exact match, wasteful queries become negatives, and any brand leakage is flagged.' },
  { channel: 'search', stage: 2, title: '2+1 architecture in place', description: 'Brand + Prospecting + one earned strategic layer (location, product, audience-signal, or remarketing) all live, with the +1 only added once the core is performing.' },
  // Google Search — Stage 3 (Scale, 50+ conv/month, CPA stable)
  { channel: 'search', stage: 3, title: 'Target CPA bidding on core campaign', description: 'Target CPA set at roughly the current CPA × 1.1, then tightened 5–8% per week. Requires CPA within 15% of target for at least three weeks before turning on.', autoCheckKey: 'search-tcpa-bidding' },
  { channel: 'search', stage: 3, title: 'Additional strategic layers running', description: 'Beyond the first +1, the next strategic layer is earned and funded by core headroom — location, product, audience-signal, or remarketing campaigns stacked on top.' },
  { channel: 'search', stage: 3, title: 'Remarketing audiences targeting active searchers', description: 'High-performing audiences are moved from observation to targeting on prospecting campaigns, with broader match types layered so bids can climb for known visitors.' },

  // Conversion Tracking — Stage 0 (Foundation: tracking install)
  { channel: 'tracking', stage: 0, title: 'Google Ads conversion installed', description: 'Conversion tag firing on the primary conversion page.', route: '/settings/conversions', autoCheckKey: 'tracking-conversion-installed' },
  { channel: 'tracking', stage: 0, title: 'GA4 connected to Google Ads', description: 'GA4 property linked, conversions imported to Google Ads.', autoCheckKey: 'tracking-ga4-connected' },
  { channel: 'tracking', stage: 0, title: 'Primary conversion actions defined', description: 'Primary actions set; non-primary moved to Secondary.', route: '/settings/conversions', autoCheckKey: 'tracking-primary-conversions' },
  { channel: 'tracking', stage: 0, title: 'Base events firing across site', description: 'PageView, scroll, and click events firing reliably across pages.', autoCheckKey: 'tracking-base-events' },
  // Conversion Tracking — Stage 1
  { channel: 'tracking', stage: 1, title: 'Enhanced conversions enabled', description: 'Enhanced conversions live for cross-platform identity matching.' },
  { channel: 'tracking', stage: 1, title: 'Conversion values populated', description: 'Indexed or revenue values flowing on every conversion event.', autoCheckKey: 'tracking-conversion-values' },
  { channel: 'tracking', stage: 1, title: 'Micro conversions tracked', description: 'Engagement-tier events tracked for Smart Bidding signals.' },
  { channel: 'tracking', stage: 1, title: 'Cross-domain tracking verified', description: 'Multi-domain journeys preserve session and user identity.' },
  // Conversion Tracking — Stage 2
  { channel: 'tracking', stage: 2, title: 'Server-side tagging live', description: 'GTM Server-Side container deployed with first-party endpoint.' },
  { channel: 'tracking', stage: 2, title: 'Consent Mode v2 deployed', description: 'Consent Mode v2 verified across Google Ads and Analytics.', autoCheckKey: 'tracking-consent-mode-v2' },
  { channel: 'tracking', stage: 2, title: 'Deduplication across platforms', description: 'Cross-platform deduplication keeps reported conversions clean.' },
  // Conversion Tracking — Stage 3
  { channel: 'tracking', stage: 3, title: 'Quality of conversions audited', description: 'Quarterly audit confirms tag firing rate and value accuracy.' },

  // Website — Stage 1
  { channel: 'website', stage: 1, title: 'SSL active', description: 'HTTPS enforced on every page with valid certificate.', autoCheckKey: 'website-ssl-active' },
  { channel: 'website', stage: 1, title: 'Mobile-friendly verified', description: 'Mobile usability verified in Search Console and on real devices.', autoCheckKey: 'website-mobile-friendly' },
  { channel: 'website', stage: 1, title: 'Above-fold CTA on all key pages', description: 'Primary CTA visible above the fold on every key landing page.', autoCheckKey: 'website-above-fold-cta' },
  { channel: 'website', stage: 1, title: 'Core Web Vitals passing on homepage', description: 'LCP, INP, and CLS in the green for the homepage on mobile.', autoCheckKey: 'website-cwv-home' },
  // Website — Stage 2
  { channel: 'website', stage: 2, title: 'Dedicated landing pages per campaign', description: 'Each major campaign points to a tailored landing page, not the home.', autoCheckKey: 'website-dedicated-landing-pages' },
  { channel: 'website', stage: 2, title: 'Form fields optimised', description: 'Form length tested and reduced; field types match data type.', autoCheckKey: 'website-forms-optimised' },
  { channel: 'website', stage: 2, title: 'Structured data live', description: 'Organisation, product, and breadcrumb structured data validated.', autoCheckKey: 'website-structured-data' },
  { channel: 'website', stage: 2, title: 'Core Web Vitals passing across site', description: 'LCP, INP, and CLS green across the top 80% of traffic pages.', autoCheckKey: 'website-cwv-site' },
  // Website — Stage 3
  { channel: 'website', stage: 3, title: 'A/B testing framework live', description: 'Tooling and workflow in place to run statistically valid tests.' },
  { channel: 'website', stage: 3, title: 'Page speed under 3s on mobile', description: 'P75 mobile load time under 3 seconds on key pages.' },
  { channel: 'website', stage: 3, title: 'On-page chat or contact widget', description: 'Live chat or contact widget that increases qualified conversations.' },
  { channel: 'website', stage: 3, title: 'Basic personalisation', description: 'Geo or referrer-based content variations on the homepage.' },
];

export const CHANNEL_GOALS: ChannelGoal[] = RAW.map((g, idx) => ({
  ...g,
  id: `${g.channel}-s${g.stage}-${idx}`,
}));

export const TOTAL_CHANNEL_GOALS = CHANNEL_GOALS.length;

// Expected impact per (channel, stage). Ranges are blended from industry
// benchmarks (Google Ads, Meta, CRO research) and represent the cumulative
// lift versus the prior stage, not absolute account performance.
//
// Sources: WordStream / Google Ads benchmarks 2025-26 (sitelinks +10–20% CTR,
// Smart Bidding learning 4–6 wks), Optmyzr / Karooya / ALM (negative keyword
// pruning trims 20–35% waste), Triple Whale / GROAS (tROAS adds 20–35% ROAS
// once volume hits 50+ conv/mo), Modern Marketing Institute / Segwise (Meta
// lookalikes –20–40% CPA, retargeting –40–70% CPA), Google Ads docs +
// FunnelFlex (enhanced conversions +10% conv, offline import +20–30% Smart
// Bidding efficiency / +25% ROAS), Unbounce / Foundry CRO (systematic CRO
// 30–80% lift in 90 days; form trimming 25–40%).
export interface StageImpact {
  roas: string;
  // Optional: only set when the stage's actions drive direct conversion
  // lift / CPA reduction. Conversion Tracking stages omit these because
  // they improve measurement accuracy, not raw conv volume or CPA.
  conversions?: string;
  cpa?: string;
  spend: string;
  note?: string;
}
const STAGE_IMPACT_RAW: Record<StrategyChannelId, Record<ChannelStage, StageImpact | null>> = {
  // Glitch is in-platform setup/operations (connect account, onboarding,
  // invite teammates, schedule refresh). These enable performance work
  // but aren't performance drivers themselves — no defensible per-stage
  // ROAS / Conv / CPA range, so badges are suppressed.
  glitch: {
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
  },
  search: {
    0: null,
    1: { roas: '+10–20%', conversions: '+5–10%', cpa: '–10–15%', spend: '+5–10%' },
    2: { roas: '+15–25%', conversions: '+10–15%', cpa: '–15–25%', spend: 'Flat (waste cut)' },
    3: { roas: '+20–30%', conversions: '+20–30%', cpa: '–10–15%', spend: '+20–30%' },
    4: { roas: '+20–35%', conversions: '+25–40%', cpa: '–15–25%', spend: '+25–50%' },
    5: { roas: '+15–25% sustained', conversions: '+15–25%', cpa: '–10–20%', spend: '+50–100%' },
  },
  display: {
    0: null,
    1: { roas: 'Baseline', conversions: '+5–10%', cpa: 'Flat', spend: '+5–10%' },
    2: { roas: '+10–20%', conversions: '+10–15%', cpa: '–15–25%', spend: '+10–20%' },
    3: { roas: '+25–40%', conversions: '+20–30%', cpa: '–40–70% (retargeting)', spend: '+20–40%' },
    4: { roas: '+20–35%', conversions: '+20–30%', cpa: '–15–25%', spend: '+30–50%' },
    5: { roas: '+15–25%', conversions: '+10–20%', cpa: '–10–20%', spend: '+40–80%' },
  },
  meta: {
    0: null,
    1: { roas: '+10–20% (CAPI recovery)', conversions: '+10–15%', cpa: '–5–10%', spend: '+5–10%' },
    2: { roas: '+15–35%', conversions: '+15–25%', cpa: '–20–40% (lookalikes)', spend: '+15–30%' },
    3: { roas: '+20–30%', conversions: '+20–35%', cpa: '–15–25%', spend: '+20–40%' },
    4: { roas: '+15–25%', conversions: '+15–25%', cpa: '–10–20%', spend: '+25–50%' },
    5: { roas: '+10–20% sustained', conversions: '+10–20%', cpa: '–10–15%', spend: '+50–100%' },
  },
  tracking: {
    0: null,
    1: { roas: 'Baseline', spend: 'Flat' },
    2: { roas: '+5–15%', spend: 'Flat' },
    3: { roas: '+10–20%', spend: 'Flat' },
    4: { roas: '+20–30% (offline import)', spend: '+10–20%' },
    5: { roas: '+15–25%', spend: '+15–30%' },
  },
  website: {
    0: null,
    1: { roas: '+5–10%', conversions: '+5–10%', cpa: '–5–10%', spend: 'Flat' },
    2: { roas: '+15–25%', conversions: '+25–40%', cpa: '–20–30%', spend: '+10–20%' },
    3: { roas: '+15–25%', conversions: '+15–30%', cpa: '–10–20%', spend: '+15–30%' },
    4: { roas: '+15–25%', conversions: '+20–30%', cpa: '–15–25%', spend: '+25–50%' },
    5: { roas: '+10–20%', conversions: '+15–25%', cpa: '–10–20%', spend: '+30–60%' },
  },
  awards: {
    0: null,
    1: null,
    2: null,
    3: null,
    4: { roas: 'Prestige', spend: 'No direct lift', note: 'Recognition milestone' },
    5: { roas: 'Prestige', spend: 'No direct lift', note: 'Recognition milestone' },
  },
};

export function impactFor(channel: StrategyChannelId, stage: ChannelStage): StageImpact | null {
  return STAGE_IMPACT_RAW[channel]?.[stage] ?? null;
}

// Stage-level meta: expected schedule and rolled-up benefit across all
// channels. Rollups are the headline outcome of completing that stage's
// work end-to-end — not a strict sum of channel impacts (percent ranges
// don't add cleanly) but the combined story.
export interface StageMeta {
  // Phase label (no fixed week window — pacing is a live countdown
  // against `targetDay`, anchored to the org start date).
  phase: string;
  // Cumulative target: the day-since-start this stage should be reached
  // by. The UI counts down the days remaining against this.
  targetDay: number;
  rolledUp: StageImpact;
}

export const STAGE_META: Record<ChannelStage, StageMeta> = {
  0: {
    phase: 'Foundation',
    targetDay: 7,
    // Pure setup: account connection and conversion tracking install.
    // No baseline yet to measure lift against, so no performance claim.
    rolledUp: { roas: 'Not yet measurable', spend: 'Setup only' },
  },
  1: {
    phase: 'Launch',
    targetDay: 30,
    // No conv/CPA lift claimed at Stage 1: conversion tracking is being
    // installed during this phase, so there is no clean baseline to
    // measure lift against. ROAS reflects brand-campaign capture only.
    rolledUp: { roas: 'Baseline established', spend: 'Launch budget' },
  },
  2: {
    phase: 'Stabilisation',
    targetDay: 60,
    rolledUp: { roas: '+15–25%', conversions: '+15–25%', cpa: '–15–25%', spend: 'Roughly flat (waste cut)' },
  },
  3: {
    phase: 'Scale',
    targetDay: 90,
    rolledUp: { roas: '+20–30%', conversions: '+20–30%', cpa: '–10–15%', spend: '+20–30%' },
  },
  4: {
    phase: 'Established',
    targetDay: 180,
    rolledUp: { roas: '+20–35%', conversions: '+25–40%', cpa: '–15–25%', spend: '+25–50%' },
  },
  5: {
    phase: 'Market Leader',
    targetDay: 270,
    rolledUp: { roas: '+15–25% sustained', conversions: '+15–25%', cpa: '–10–20%', spend: '+50–100%' },
  },
};

export function goalsForChannel(channel: StrategyChannelId): ChannelGoal[] {
  return CHANNEL_GOALS.filter((g) => g.channel === channel);
}

// Pared-down surface: only these three channels are rendered in the trimmed
// Strategy section, in this display order. The full CHANNELS / CHANNEL_GOALS
// arrays stay intact so store atoms and any direct-URL routes keep working.
export const VISIBLE_CHANNEL_IDS: StrategyChannelId[] = ['glitch', 'tracking', 'search', 'website'];

export const VISIBLE_CHANNELS: ChannelMeta[] = VISIBLE_CHANNEL_IDS
  .map((id) => CHANNELS.find((c) => c.id === id))
  .filter((c): c is ChannelMeta => c !== undefined);

export const VISIBLE_CHANNEL_GOALS: ChannelGoal[] = CHANNEL_GOALS.filter((g) =>
  VISIBLE_CHANNEL_IDS.includes(g.channel),
);
